import { utils } from "@rjsf/core";
import {
  MarketplaceFormElement,
  MarketplaceFormElementType,
  MarketplaceFormElementWidget,
} from "../../schema/generated/schema";
import { CustomFieldTemplate } from "./CustomFieldTemplate";

const mapElementTypeToLoftWidget = {
  [MarketplaceFormElementType.Bool]: "CheckboxWidget",
  [MarketplaceFormElementType.Currency]: "CurrencyWidget",
  [MarketplaceFormElementType.Date]: "DatePickerWidget",
  [MarketplaceFormElementType.MultiSelect]: "MultiSelectWidget",
  [MarketplaceFormElementType.Number]: "TextWidget",
  [MarketplaceFormElementType.Select]: "SelectWidget",
  [MarketplaceFormElementType.Text]: "TextWidget",
};

const mapElementWidgetToLoftWidget = {
  [MarketplaceFormElementWidget.Checkboxes]: "CheckboxGroupWidget",
  [MarketplaceFormElementWidget.DatePicker]: "DatePickerWidget",
  [MarketplaceFormElementWidget.DatePickerDaysIndicator]: "DatePickerWidget",
  [MarketplaceFormElementWidget.Phone]: "PhoneNumberWidget",
  [MarketplaceFormElementWidget.ShortPlainText]: "ShortTextWidget",
  [MarketplaceFormElementWidget.Textarea]: "TextareaWidget",
};

const mapElementTypeToLoftField = {
  [MarketplaceFormElementType.Address]: "AddressField",
  [MarketplaceFormElementType.File]: "FileField",
};

const defaultUiOptions = {
  short: false,
  daysDiffIndicator: false,
  enumOptOverride: null,
};

const extraUiOptionsByElementWidget = {
  [MarketplaceFormElementWidget.ShortPlainText]: {
    short: true,
  },
  [MarketplaceFormElementWidget.Phone]: {
    short: true,
  },
  [MarketplaceFormElementWidget.ShortPlainTextNoLabel]: {
    short: true,
    hideLabel: true,
  },
  [MarketplaceFormElementWidget.WithoutLabel]: {
    hideLabel: true,
  },
  [MarketplaceFormElementWidget.DatePicker]: {
    short: true,
  },
  [MarketplaceFormElementWidget.DatePickerDaysIndicator]: {
    short: true,
    daysDiffIndicator: true,
  },
};

const extraUiOptionsByElementType = (elem: MarketplaceFormElement) => {
  switch (elem.type) {
    case MarketplaceFormElementType.Number:
    case MarketplaceFormElementType.Currency:
      return { short: true };
    case MarketplaceFormElementType.File:
      return { accept: elem.allowedFileExtensions?.toString() };
    case MarketplaceFormElementType.Date:
      return { dateOptionValues: elem.dateOptionValues };
    default:
      return {};
  }
};

export const getUiWidgetForElement = element => {
  let loftWidgetCandidate = "TextWidget";

  if (element.widget && mapElementWidgetToLoftWidget[element.widget]) {
    loftWidgetCandidate = mapElementWidgetToLoftWidget[element.widget];
  } else if (element.type && mapElementTypeToLoftWidget[element.type]) {
    loftWidgetCandidate = mapElementTypeToLoftWidget[element.type];
  }

  if (
    element.type === MarketplaceFormElementType.Select &&
    element.widget === MarketplaceFormElementWidget.Checkboxes
  ) {
    return "RadioWidget";
  }

  return loftWidgetCandidate;
};

export const getCustomFieldTemplate = () => {
  return CustomFieldTemplate;
};

export const getUiFieldForElement = element => {
  return mapElementTypeToLoftField[element.type] || null;
};

export const getUiOptionsForElement = element => {
  const extraByType = extraUiOptionsByElementType(element);
  const extraByWidget = extraUiOptionsByElementWidget[element.widget] || {};

  return {
    ...defaultUiOptions,
    ...extraByType,
    ...extraByWidget,
    grid: element.grid,
    tooltip: element.tooltip,
  };
};

export const transformErrors = (errors, schema) => {
  return errors.reduce((acc, error) => {
    const component = schema.properties[error.property?.slice?.(1)];
    let updatedMessage = error.message?.toString();

    if (component && Object.keys(component)?.length > 0) {
      const { title } = component;

      if (updatedMessage === "should NOT be shorter than 1 characters") {
        updatedMessage = `${title} is required`;
      }

      if (error.name === "pattern") {
        updatedMessage = `${title} is invalid`;
      } else if (error.name === "required") {
        updatedMessage = `This answer is required to continue.`;
      } else if (error.name === "minItems") {
        updatedMessage = `This answer is required to continue.`;
      } else if (error.name === "type") {
        if (error.message === "should be string") {
          updatedMessage = `${title} is invalid`;
        } else if (error.message === "should be number") {
          updatedMessage = `${title} must be a number`;
        } else if (error.message === "should be array") {
          return acc;
        }
      } else if (error.name === "minimum") {
        updatedMessage = updatedMessage.replace(
          ">=",
          "greater than or equal to"
        );
      } else if (error.name === "maximum") {
        updatedMessage = updatedMessage.replace("<=", "less than or equal to");
      } else if (error.name === "enum") {
        if (error.message === "should be equal to one of the allowed values") {
          updatedMessage = `Invalid selection for ${title}`;
        }
      }
    }

    acc.push({ ...error, message: updatedMessage });
    return acc;
  }, []);
};

export const getEnumOptions = (schema: any, options: Record<string, any>) => {
  if (Array.isArray(options.enumOptOverride)) {
    return options.enumOptOverride.map(opt => ({
      value: opt.value || opt,
      label: opt.label || opt.value || opt,
    }));
  }

  if (Array.isArray(options.enumOptions)) {
    return options.enumOptions.map(opt => ({
      value: opt.value || opt,
      label: opt.label || opt.value || opt,
    }));
  }

  if (typeof schema.items !== "undefined" && !Array.isArray(schema.items)) {
    return utils.optionsList(schema.items);
  }

  if (schema.enum || schema.oneOf || schema.anyOf) {
    return utils.optionsList(schema);
  }

  return [];
};

export const emptyAddressObject = {
  name: "",
  street1: "",
  street2: "",
  city: "",
  state: "AL",
  zipcode: "",
  country: "",
  phoneNumber: "",
  emailAddress: "",
};
